/* SPEC-146 — Rulings Desk shared state.
 *
 * Mirrors the TourProvider idiom: one provider holds the pending decisions, the
 * user's recent answers, and the open/snooze state; RulingsPill and RulingsDesk read
 * it. Auto-opens the desk at most once per browser session, and only when there is new
 * unanswered content the user hasn't snoozed (signature-based, per §3.1). Everything
 * here is read-and-record only — an answer is a logged ruling awaiting Param's
 * application, never a behaviour change. */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import {
  fetchRulingsPending,
  fetchRulingsMine,
  submitRulingAnswer,
  recordProvisionalDecision,
  rerouteRuling,
  RulingCard,
  RulingMineItem,
} from '@/lib/api';

interface RulingsContextType {
  pending: RulingCard[];
  mine: RulingMineItem[];
  count: number;
  deskOpen: boolean;
  loading: boolean;
  /** True when the last pending-load failed — lets callers show an honest error state
   * instead of mistaking a fetch failure for "nothing pending" (SPEC-148 review). */
  error: boolean;
  openDesk: () => void;
  closeDesk: () => void;
  snooze: () => void;
  refresh: () => void;
  discussed: Set<string>;
  talkThrough: (card: RulingCard) => void;
  answer: (
    gapId: string,
    body: { option_key?: string | null; answer_text?: string | null; concurrence?: boolean },
  ) => Promise<void>;
  // SPEC-327: admin-only — record a provisional decision on an overdue card, then refresh.
  recordProvisional: (gapId: string, decision: string, rationale?: string) => Promise<void>;
  // SPEC-584: "Not my decision" — pass the card on (recorded only), then refresh.
  reroute: (gapId: string, toUsername: string, note?: string, keepMe?: boolean) => Promise<void>;
}

const RulingsContext = createContext<RulingsContextType | null>(null);

export function useRulings() {
  const ctx = useContext(RulingsContext);
  if (!ctx) throw new Error('useRulings must be used within a RulingsProvider');
  return ctx;
}

// Signature of the pending set — changes when new cards appear or a card is materially
// reworded (version bump). A snooze stored against an old signature no longer suppresses.
function pendingSignature(cards: RulingCard[]): string {
  return cards
    .map((c) => `${c.gap_id}:${c.version}`)
    .sort()
    .join('|');
}

export function RulingsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [pending, setPending] = useState<RulingCard[]>([]);
  const [mine, setMine] = useState<RulingMineItem[]>([]);
  const [deskOpen, setDeskOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [discussed, setDiscussed] = useState<Set<string>>(new Set());
  // Once-per-session guard so a page reload doesn't re-pop the desk mid-session.
  const autoShownRef = useRef(false);

  const snoozeKey = user ? `rulings-desk-snooze-${user.username}` : null;

  const load = useCallback(async () => {
    if (!user) {
      setPending([]);
      setMine([]);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const [p, m] = await Promise.all([fetchRulingsPending(), fetchRulingsMine()]);
      setPending(p.cards || []);
      setMine(m.answers || []);
      return p.cards || [];
    } catch {
      // Non-critical surface — a failed load just means no desk this time. Flag it so
      // callers don't read the empty pending set as a confirmed "nothing waiting".
      setPending([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch on login; auto-open once if there is un-snoozed pending content.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cards = await load();
      if (cancelled || !cards || cards.length === 0 || !snoozeKey) return;
      if (autoShownRef.current) return;
      let snoozedSig: string | null = null;
      try {
        snoozedSig = localStorage.getItem(snoozeKey);
      } catch {
        snoozedSig = null;
      }
      if (snoozedSig !== null && snoozedSig === pendingSignature(cards)) return; // snoozed this exact set
      autoShownRef.current = true;
      setDeskOpen(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const openDesk = useCallback(() => setDeskOpen(true), []);
  const closeDesk = useCallback(() => setDeskOpen(false), []);

  const snooze = useCallback(() => {
    // "Later" — record the current pending signature so we don't re-pop until it changes.
    if (snoozeKey) {
      try {
        localStorage.setItem(snoozeKey, pendingSignature(pending));
      } catch {
        /* storage unavailable — desk just re-pops next session, harmless */
      }
    }
    autoShownRef.current = true;
    setDeskOpen(false);
  }, [snoozeKey, pending]);

  const talkThrough = useCallback((card: RulingCard) => {
    // Reuse the existing copilot (SPEC-123 event) — no second chat, no backend mode.
    // The framing is the card's own trusted content; the copilot answers it grounded.
    const opts = card.options.map((o) => `${o.key}) ${o.label}${o.detail ? ` — ${o.detail}` : ''}`).join('  ');
    const text =
      `I'm looking at the decision "${card.title}" (${card.gap_id}). ` +
      `In short: ${card.what_this_is} ` +
      `What runs today: ${card.running_today} ` +
      `The options are: ${opts}. ` +
      `Help me think through which to choose — what would each option actually mean, and what do you recommend?`;
    setDiscussed((prev) => {
      const next = new Set(prev);
      next.add(card.gap_id);
      return next;
    });
    window.dispatchEvent(new CustomEvent('suryaiq:copilot-ask', { detail: { text } }));
    setDeskOpen(false); // let the chat drawer show; the pill re-opens the still-answerable card
  }, []);

  const answer = useCallback(
    async (
      gapId: string,
      body: { option_key?: string | null; answer_text?: string | null; concurrence?: boolean },
    ) => {
      await submitRulingAnswer(gapId, { ...body, discussed: discussed.has(gapId) });
      await load(); // refresh pending + recent answers
    },
    [discussed, load],
  );

  const recordProvisional = useCallback(
    async (gapId: string, decision: string, rationale?: string) => {
      await recordProvisionalDecision(gapId, { decision, rationale: rationale || null });
      await load(); // refresh so the card now shows the provisional state
    },
    [load],
  );

  const reroute = useCallback(
    async (gapId: string, toUsername: string, note?: string, keepMe?: boolean) => {
      await rerouteRuling(gapId, { to_username: toUsername, note: note || null, keep_me: !!keepMe });
      await load(); // the card leaves this desk (unless kept) once the overlay is applied
    },
    [load],
  );

  const value: RulingsContextType = {
    pending,
    mine,
    count: pending.length,
    deskOpen,
    loading,
    error,
    openDesk,
    closeDesk,
    snooze,
    refresh: load,
    discussed,
    talkThrough,
    answer,
    recordProvisional,
    reroute,
  };

  return <RulingsContext.Provider value={value}>{children}</RulingsContext.Provider>;
}
